import { 
  captureError, 
  captureMessage, 
  addBreadcrumb, 
  setSentryContext,
  setSentryTag 
} from '@/lib/sentry';
import type { 
  ErrorCategory, 
  ErrorContext, 
  ApiCallContext, 
  UserActionContext,
  SentryBreadcrumbCategory 
} from '@/types/sentry';

/**
 * 예약 관련 에러 추적
 */
export const trackBookingError = (
  error: Error, 
  context: {
    bookingId?: string;
    step?: 'create' | 'update' | 'cancel' | 'payment';
    userId?: string;
    additionalData?: Record<string, any>;
  }
) => {
  setSentryTag('error_category', 'booking');
  setSentryTag('booking_step', context.step || 'unknown');
  
  setSentryContext('booking_error', {
    bookingId: context.bookingId,
    step: context.step,
    userId: context.userId,
    ...context.additionalData,
  });

  addBreadcrumb({
    category: 'booking',
    message: `Booking error at step: ${context.step}`,
    level: 'error',
    data: context,
  });

  return captureError(error, { booking_error: context });
};

/**
 * 결제 관련 에러 추적
 */
export const trackPaymentError = (
  error: Error,
  context: {
    paymentMethod?: string;
    amount?: number;
    bookingId?: string;
    tossPaymentKey?: string;
    additionalData?: Record<string, any>;
  }
) => {
  setSentryTag('error_category', 'payment');
  setSentryTag('payment_method', context.paymentMethod || 'unknown');
  
  setSentryContext('payment_error', {
    paymentMethod: context.paymentMethod,
    amount: context.amount,
    bookingId: context.bookingId,
    tossPaymentKey: context.tossPaymentKey,
    ...context.additionalData,
  });

  addBreadcrumb({
    category: 'payment',
    message: `Payment error with method: ${context.paymentMethod}`,
    level: 'error',
    data: context,
  });

  return captureError(error, { payment_error: context });
};

/**
 * 인증 관련 에러 추적
 */
export const trackAuthError = (
  error: Error,
  context: {
    action?: 'login' | 'logout' | 'signup' | 'refresh';
    provider?: string;
    userId?: string;
    additionalData?: Record<string, any>;
  }
) => {
  setSentryTag('error_category', 'authentication');
  setSentryTag('auth_action', context.action || 'unknown');
  
  setSentryContext('auth_error', {
    action: context.action,
    provider: context.provider,
    userId: context.userId,
    ...context.additionalData,
  });

  addBreadcrumb({
    category: 'auth',
    message: `Auth error during: ${context.action}`,
    level: 'error',
    data: context,
  });

  return captureError(error, { auth_error: context });
};

/**
 * API 호출 추적 헬퍼
 */
export const trackApiCall = (context: ApiCallContext) => {
  addBreadcrumb({
    category: 'api',
    message: `${context.method} ${context.endpoint}`,
    level: context.error ? 'error' : 'info',
    data: {
      endpoint: context.endpoint,
      method: context.method,
      status: context.status,
      duration: context.duration,
      hasError: !!context.error,
    },
  });

  if (context.error) {
    setSentryTag('api_endpoint', context.endpoint);
    setSentryTag('api_method', context.method);
    setSentryTag('api_status', context.status?.toString() || 'unknown');
    
    return captureError(context.error, { api_call: context });
  }
};

/**
 * 사용자 액션 추적 헬퍼
 */
export const trackUserAction = (context: UserActionContext) => {
  addBreadcrumb({
    category: 'user',
    message: `User action: ${context.action}`,
    level: 'info',
    data: {
      action: context.action,
      component: context.component,
      ...context.data,
    },
  });

  setSentryContext('user_action', {
    action: context.action,
    component: context.component,
    timestamp: new Date().toISOString(),
    ...context.data,
  });
};

/**
 * UI 인터랙션 추적
 */
export const trackUIInteraction = (
  element: string,
  action: string,
  data?: Record<string, any>
) => {
  addBreadcrumb({
    category: 'ui',
    message: `UI: ${action} on ${element}`,
    level: 'info',
    data: {
      element,
      action,
      ...data,
    },
  });
};

/**
 * 성능 이슈 추적
 */
export const trackPerformanceIssue = (
  metric: string,
  value: number,
  threshold: number,
  context?: Record<string, any>
) => {
  if (value > threshold) {
    setSentryTag('performance_issue', metric);
    
    captureMessage(
      `Performance issue: ${metric} (${value}ms) exceeded threshold (${threshold}ms)`,
      'warning',
      {
        performance_issue: {
          metric,
          value,
          threshold,
          ...context,
        },
      }
    );
  }
};

/**
 * 비즈니스 로직 에러 추적
 */
export const trackBusinessLogicError = (
  operation: string,
  error: Error,
  context?: Record<string, any>
) => {
  setSentryTag('error_category', 'business_logic');
  setSentryTag('business_operation', operation);
  
  addBreadcrumb({
    category: 'system',
    message: `Business logic error in: ${operation}`,
    level: 'error',
    data: context,
  });

  return captureError(error, { business_logic_error: { operation, ...context } });
};

/**
 * React Query 에러 추적
 */
export const trackReactQueryError = (
  queryKey: string[],
  error: Error,
  variables?: any
) => {
  setSentryTag('error_category', 'react_query');
  setSentryTag('query_key', queryKey.join('.'));
  
  setSentryContext('react_query_error', {
    queryKey,
    variables,
    error: error.message,
  });

  addBreadcrumb({
    category: 'query',
    message: `React Query error: ${queryKey.join('.')}`,
    level: 'error',
    data: {
      queryKey,
      variables,
      error: error.message,
    },
  });

  return captureError(error, { react_query_error: { queryKey, variables } });
};

/**
 * 카테고리별 에러 분류 및 추적
 */
export const trackCategorizedError = (
  category: ErrorCategory,
  error: Error,
  context?: ErrorContext
) => {
  setSentryTag('error_category', category);
  
  const breadcrumbCategory: SentryBreadcrumbCategory = 
    category === 'authentication' || category === 'authorization' ? 'auth' :
    category === 'booking' ? 'booking' :
    category === 'payment' ? 'payment' :
    category === 'network' ? 'api' :
    category === 'ui' ? 'ui' :
    'system';

  addBreadcrumb({
    category: breadcrumbCategory,
    message: `${category} error: ${error.message}`,
    level: 'error',
    data: context,
  });

  if (context) {
    setSentryContext('categorized_error', context);
  }

  return captureError(error, { categorized_error: { category, ...context } });
};

/**
 * 개발 환경에서만 실행되는 디버그 추적
 */
export const trackDebugInfo = (message: string, data?: Record<string, any>) => {
  if (import.meta.env.DEV) {
    addBreadcrumb({
      category: 'system',
      message: `Debug: ${message}`,
      level: 'debug',
      data,
    });
  }
};

/**
 * 사용자 피드백과 함께 에러 리포트
 */
export const reportErrorWithFeedback = (
  error: Error,
  userFeedback: string,
  context?: Record<string, any>
) => {
  setSentryContext('user_feedback', {
    feedback: userFeedback,
    timestamp: new Date().toISOString(),
    ...context,
  });

  addBreadcrumb({
    category: 'user',
    message: 'User provided feedback with error report',
    level: 'info',
    data: {
      feedbackLength: userFeedback.length,
      hasContext: !!context,
    },
  });

  return captureError(error, { 
    user_feedback: { 
      feedback: userFeedback, 
      ...context 
    } 
  });
};